import { Entity } from '@app/core';
import {
  IDecision,
  IDecisionField,
  IDecisionModule,
  IQuizChoice,
  isQuizField,
  isNumericField,
} from '@games/interfaces';

export class DecisionField extends Entity<IDecisionField> {
  constructor(props: IDecisionField) {
    super(props);
  }

  // Common properties
  get name(): string {
    return this.props.name;
  }

  get code(): string {
    return this.props.fieldName;
  }

  get description(): string {
    return this.props.description;
  }

  get visible(): boolean {
    return !!this.props.visible;
  }

  get textbookAnchors() {
    return this.props.textbookAnchors;
  }

  // Field type discriminator
  get fieldType(): 'numeric' | 'quiz' {
    return this.props.fieldType === 'quiz' ? 'quiz' : 'numeric';
  }

  get isQuiz(): boolean {
    return isQuizField(this.props);
  }

  get isNumeric(): boolean {
    return isNumericField(this.props);
  }

  // Numeric field properties (return defaults for quiz fields)
  get value(): number {
    if (isNumericField(this.props)) {
      return this.props.value;
    }
    return 0;
  }

  get previousValue(): number {
    if (isNumericField(this.props)) {
      return this.props.previousValue;
    }
    return 0;
  }

  get possibleValues(): Record<string, number> | undefined {
    if (isNumericField(this.props)) {
      return this.props.dropdown;
    }
    return undefined;
  }

  get precis(): number {
    if (isNumericField(this.props)) {
      return this.props.precis;
    }
    return 0;
  }

  get min(): number {
    if (isNumericField(this.props)) {
      return this.props.min;
    }
    return 0;
  }

  get max(): number {
    if (isNumericField(this.props)) {
      return this.props.max;
    }
    return 0;
  }

  // Quiz field properties (return defaults for numeric fields)
  get question(): string {
    if (isQuizField(this.props)) {
      return this.props.question;
    }
    return '';
  }

  get choices(): IQuizChoice[] {
    if (isQuizField(this.props)) {
      return this.props.choices;
    }
    return [];
  }

  get selectedChoice(): string | null {
    if (isQuizField(this.props)) {
      return this.props.selectedChoice;
    }
    return null;
  }

  get rationale(): string {
    if (isQuizField(this.props)) {
      return this.props.rationale;
    }
    return '';
  }

  get rationaleMaxLength(): number {
    if (isQuizField(this.props)) {
      return this.props.rationaleMaxLength ?? 2000;
    }
    return 2000;
  }
}

export class DecisionModule extends Entity<IDecisionModule> {
  constructor(props: IDecisionModule) {
    super(props);
  }

  get name(): string {
    return this.props.moduleName;
  }

  get description(): string {
    return this.props.description;
  }

  get fields(): DecisionField[] {
    return (
      this.props.fields &&
      this.props.fields
        .map((field: IDecisionField) => new DecisionField(field))
        .filter((field: DecisionField) => field.visible)
    );
  }
}

export class Decision extends Entity<IDecision> {
  constructor(props: IDecision) {
    super(props);
  }

  get category(): string {
    return this.props.category;
  }

  get modules(): DecisionModule[] {
    return (
      this.props.modules &&
      this.props.modules
        .map((module: IDecisionModule) => new DecisionModule(module))
        .filter((module: DecisionModule) => module.fields.length)
    );
  }
}
